// The one markdown converter and sanitiser: notes, AI output, the support
// assistant and confirmation dialogs all render through here. The rules are in
// docs/html-safety.md.

import createDOMPurify, { type Config, type WindowLike } from 'dompurify';
import * as showdown from 'showdown';
import { escapeHtml } from './html-escape';

export interface SanitizeOptions {
    /**
     * Keep http and https images. Off by default: an image loads as soon as it
     * is shown, so one in model output sends its URL, and any plan text the
     * model was steered into putting there, without a click.
     */
    images?: boolean;
}

/** Returns HTML that is safe to insert, whatever `dirty` contains. */
export type HtmlSanitizer = (dirty: string, options?: SanitizeOptions) => string;

const LINK_SCHEMES = /^(?:https?|mailto):/i;
const IMAGE_SCHEMES = /^https?:/i;
// Showdown's table column alignment, the only style kept. An align attribute
// instead would lose to Bootstrap's `th { text-align: left }`.
const CELL_ALIGN_STYLE = /^text-align\s*:\s*(left|right|center)\s*;?$/i;

// The AI renderer's click-to-select links (IntelligencePanelComponent.setupClickDelegation).
const ENTITY_LINK_CLASS = 'gp-entity-link';

const TAGS = [
    'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'b', 'em', 'i', 'u', 'del', 's', 'strike', 'sub', 'sup', 'code', 'pre', 'blockquote', 'kbd',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'span',
];

const CONFIG: Config = {
    ALLOWED_TAGS: TAGS,
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'class', 'data-entity-type', 'data-entity-id'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOWED_URI_REGEXP: LINK_SCHEMES,
    // Not URIs: without this the scheme check above strips them.
    ADD_URI_SAFE_ATTR: ['data-entity-type', 'data-entity-id'],
};

const CONFIG_WITH_IMAGES: Config = { ...CONFIG, ALLOWED_TAGS: [...TAGS, 'img'] };

function failClosed(dirty: string): string {
    return escapeHtml(dirty);
}

function isTableCell(node: Element): boolean {
    return node.nodeName === 'TH' || node.nodeName === 'TD';
}

/**
 * A sanitiser bound to `win`'s DOM. Where there is no usable DOM (Node, the
 * Functions host) it escapes its input instead, so it never passes markup
 * through unchecked.
 */
export function createHtmlSanitizer(win: WindowLike | null | undefined): HtmlSanitizer {
    if (!win) return failClosed;
    // A separate instance: formiojs sanitises with the default export, and
    // these hooks would change its output too.
    const purify = createDOMPurify(win);
    if (!purify.isSupported || typeof purify.sanitize !== 'function') return failClosed;

    purify.addHook('uponSanitizeAttribute', (node, data) => {
        const value = data.attrValue.trim();
        if (data.attrName === 'src') {
            data.keepAttr = IMAGE_SCHEMES.test(value);
        } else if (data.attrName === 'href') {
            data.keepAttr = LINK_SCHEMES.test(value);
        } else if (data.attrName === 'style') {
            const alignment = isTableCell(node) ? CELL_ALIGN_STYLE.exec(value) : null;
            data.keepAttr = !!alignment;
            if (alignment) data.attrValue = `text-align:${alignment[1].toLowerCase()};`;
        } else if (data.attrName === 'class') {
            data.keepAttr = node.nodeName === 'SPAN' && value === ENTITY_LINK_CLASS;
        } else if (data.attrName.startsWith('data-entity-')) {
            data.keepAttr = node.nodeName === 'SPAN';
        }
    });
    purify.addHook('afterSanitizeAttributes', node => {
        if (node.nodeName === 'A' && node.hasAttribute('href')) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });

    return (dirty, options) => purify.sanitize(dirty ?? '', options?.images ? CONFIG_WITH_IMAGES : CONFIG);
}

let pageSanitizer: { win: WindowLike; sanitize: HtmlSanitizer } | undefined;

/** Sanitises with the page's own DOM; see createHtmlSanitizer. */
export function sanitizeHtml(dirty: string, options?: SanitizeOptions): string {
    const win: WindowLike | undefined = typeof window === 'undefined' ? undefined : window;
    if (!win) return failClosed(dirty);
    if (pageSanitizer?.win !== win) {
        pageSanitizer = { win, sanitize: createHtmlSanitizer(win) };
    }
    return pageSanitizer.sanitize(dirty, options);
}

const markdownConverter = new showdown.Converter({
    simplifiedAutoLink: true,
    tables: true,
    // Keep three-space-indented bullets nested so ordered-list numbering does not restart.
    disableForced4SpacesIndentedSublists: true,
    noHeaderId: true,
});

export interface MarkdownToSafeHtmlOptions extends SanitizeOptions {
    /** Runs on the converted HTML before it is sanitised. */
    transformHtml?: (html: string) => string;
    /** Defaults to sanitizeHtml. */
    sanitize?: HtmlSanitizer;
}

/**
 * Markdown to HTML that is safe to insert. Raw HTML in the markdown is
 * sanitised to the same allowlist, not shown literally.
 */
export function markdownToSafeHtml(markdown: string | null | undefined, options: MarkdownToSafeHtmlOptions = {}): string {
    let html = markdownConverter.makeHtml(markdown ?? '');
    if (options.transformHtml) {
        html = options.transformHtml(html);
    }
    return (options.sanitize ?? sanitizeHtml)(html, { images: options.images });
}

/**
 * The text of an HTML string, parsed in a document that is never rendered, so
 * nothing in it loads or runs. A detached element in the page does not give
 * that guarantee: an <img> assigned to its innerHTML still fetches, and fires
 * onerror.
 */
export function htmlToText(html: string, parser: DOMParser = new DOMParser()): string {
    return parser.parseFromString(html, 'text/html').body.textContent ?? '';
}
